import type { CompositeFilterDescriptor, DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridNoRecords, type GridCellProps, type GridCustomCellProps, type GridDataStateChangeEvent, type GridDetailExpandChangeEvent, type GridDetailRowProps, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { NumericTextBox, type NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { copyIcon, eyeIcon, trashIcon } from "@progress/kendo-svg-icons";
import { skipToken } from "@reduxjs/toolkit/query";
import { createContext, useCallback, useContext, useState } from "react";
import { useCloneChatConversationMutation, useDeleteAdminChatConversationMessageMutation, useListAdminChatConversationMessagesQuery, useListAdminChatConversationsQuery } from "../../api/admin/adminChatConversationApi";
import { useAppDispatch } from "../../app/hooks";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useListUsersQuery } from "../../api/admin/adminUserApi";
import ContentVisualizer, { type ContentVisualizerProps } from "../../components/dialog/contentVisualizer";
import AppGrid from "../../components/grid/appGrid";
import { notify } from "../../components/notify";
import { ChatMessageType, type AdminChatConversationDto, type AdminChatConversationMessageDto, type CustomerDto, type UserDto } from "../../contracts";
import { getChatMessageTypeLabel } from "../../utils/chatMessageUtils";
import { formatDateTime, toDate } from "../../utils/dateValueConversion";

const initialSort: SortDescriptor[] = [{ field: "lastMessageAt", dir: "desc" }];
const messageInitialSort: SortDescriptor[] = [{ field: "sequence", dir: "asc" }];
const selectorRequest: DataSourceRequestState = { skip: 0, take: 1000, sort: [{ field: "name", dir: "asc" }] };
const userSelectorRequest = (customer: CustomerDto | null): DataSourceRequestState | typeof skipToken =>
    customer == null
        ? skipToken
        : {
                skip: 0, take: 1000, sort: [{ field: "email", dir: "asc" }],
                filter: {
                    logic: "and",
                    filters: [{ field: "customerId", operator: "eq", value: customer.id }]
                }
            };

const buildFilter = (conversationId: number | null, customer: CustomerDto | null, user: UserDto | null, dataStateFilter?: CompositeFilterDescriptor): CompositeFilterDescriptor => ({
    logic: "and",
    filters: [
        ...(dataStateFilter == null ? [] : [dataStateFilter]),
        ...(conversationId == null ? [] : [{ field: "id", operator: "eq", value: conversationId }]),
        ...(customer == null ? [] : [{ field: "user.customerId", operator: "eq", value: customer.id }]),
        ...(user == null ? [] : [{ field: "userId", operator: "eq", value: user.id }])
    ]
});

const DateCell = <T extends object>(field: keyof T): React.FC<GridCellProps> => props => {
    const value = (props.dataItem as T)[field] as Date | string | null | undefined;
    const date = toDate(value);
    return <td className="k-table-td">{date == null ? "-" : formatDateTime(date)}</td>;
};

const ChatMessageTypeCell = (props: GridCellProps): React.ReactElement => (
    <td>{getChatMessageTypeLabel(props.dataItem.type as ChatMessageType)}</td>
);

type ContentMode = "text" | "json";

type SelectedContent = {
    content: string;
    mode: ContentMode;
    title: string;
};

type OpenContentVisualizer = (selection: SelectedContent) => void;

const OpenContentVisualizerContext = createContext<OpenContentVisualizer | null>(null);

const ContentPreviewCell = ({ props, value, mode, title, ariaLabel }: { props: GridCustomCellProps; value: string; mode: ContentMode; title: string; ariaLabel: string }): React.ReactElement => {
    const openContentVisualizer = useContext(OpenContentVisualizerContext);
    const cellClassName = [props.tdProps?.className, "k-table-td overflow-hidden"].filter(Boolean).join(" ");
    const message = props.dataItem as AdminChatConversationMessageDto;

    return (
        <td {...props.tdProps} className={cellClassName}>
            <div className="d-flex align-items-center gap-2 w-100 overflow-hidden" style={{ minWidth: 0 }}>
                <span className="d-block flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                    {value || "-"}
                </span>
                {value && (
                    <Button
                        className="flex-shrink-0"
                        type="button"
                        size="small"
                        themeColor="primary"
                        svgIcon={eyeIcon}
                        title={ariaLabel}
                        aria-label={ariaLabel}
                        onClick={() => openContentVisualizer?.({ content: value, mode, title: `${title} - Message #${message.id}` })}
                    />
                )}
            </div>
        </td>
    );
};

const TextCell = (props: GridCustomCellProps): React.ReactElement => {
    const text = (props.dataItem as AdminChatConversationMessageDto).text ?? "";

    return <ContentPreviewCell props={props} value={text} mode="text" title="Message Text" ariaLabel="View text" />;
};

const RouteCell = (props: GridCustomCellProps): React.ReactElement => {
    const text = (props.dataItem as AdminChatConversationMessageDto).view_route ?? "";

    return <ContentPreviewCell props={props} value={text} mode="text" title="View Route" ariaLabel="View route" />;
};

const ViewContextJsonCell = (props: GridCustomCellProps): React.ReactElement => {
    const json = (props.dataItem as AdminChatConversationMessageDto).view_context_json ?? "";

    return <ContentPreviewCell props={props} value={json} mode="json" title="View Context JSON" ariaLabel="View context JSON" />;
};
const JsonCell = (props: GridCustomCellProps): React.ReactElement => {
    const json = (props.dataItem as AdminChatConversationMessageDto).json ?? "";

    return <ContentPreviewCell props={props} value={json} mode="json" title="Message JSON" ariaLabel="View message JSON" />;
};

const getMutationErrorMessage = (error: unknown, action: "clone" | "delete"): string => {
    const defaultMessage = action === "clone"
        ? "Unable to clone the conversation. Please try again."
        : "Unable to delete the message. Please try again.";

    if (typeof error !== "object" || error === null || !("status" in error)) {
        return defaultMessage;
    }

    if (error.status === 404) {
        return action === "clone"
            ? "The conversation was not found. It may have already been removed."
            : "The message was not found. It may have already been deleted.";
    }

    const data = "data" in error ? error.data : null;
    const serverMessage = typeof data === "object" && data !== null && "error_message" in data && typeof data.error_message === "string"
        ? data.error_message
        : null;

    if (error.status === 409) {
        return serverMessage ?? "The message could not be deleted because it conflicts with related records.";
    }

    return serverMessage ?? defaultMessage;
};

const ConversationMessages: React.FC<GridDetailRowProps> = props => {
    const conversation = props.dataItem as AdminChatConversationDto;
    const dispatch = useAppDispatch();
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 10, sort: messageInitialSort });
    const [selectedContent, setSelectedContent] = useState<SelectedContent | null>(null);
    const [messageToDelete, setMessageToDelete] = useState<AdminChatConversationMessageDto | null>(null);
    const { data, isFetching, isError } = useListAdminChatConversationMessagesQuery({ ...dataState, chat_conversation_id: conversation.id });
    const [deleteMessage, { isLoading: isDeleting }] = useDeleteAdminChatConversationMessageMutation();
    const openContentVisualizer = useCallback((selection: SelectedContent): void => setSelectedContent(selection), []);
    const closeContentVisualizer = useCallback((): void => setSelectedContent(null), []);
    const contentVisualizerProps: ContentVisualizerProps = selectedContent?.mode === "json"
        ? { isOpen: true, mode: "json", content: selectedContent.content, title: selectedContent.title, onClose: closeContentVisualizer }
        : { isOpen: selectedContent !== null, mode: "text", content: selectedContent?.content, title: selectedContent?.title, onClose: closeContentVisualizer };

    const handleDeleteAsync = async (): Promise<void> => {
        if (messageToDelete == null || isDeleting) {
            return;
        }

        const messageId = messageToDelete.id;
        try {
            const response = await deleteMessage({
                chat_conversation_id: conversation.id,
                chat_conversation_message_id: messageId
            }).unwrap();

            if (!response.is_success) {
                dispatch(notify(response.error_message ?? "Unable to delete the message.", "error"));
                return;
            }

            setMessageToDelete(null);
            dispatch(notify(`Message #${messageId} was permanently deleted from conversation #${conversation.id}.`, "success"));
        } catch (error) {
            dispatch(notify(getMutationErrorMessage(error, "delete"), "error"));
        }
    };

    const DeleteMessageCell = (cellProps: GridCellProps): React.ReactElement => {
        const message = cellProps.dataItem as AdminChatConversationMessageDto;

        return (
            <td className="k-table-td text-center align-middle">
                <Button
                    type="button"
                    themeColor="error"
                    fillMode="outline"
                    size="small"
                    svgIcon={trashIcon}
                    disabled={isDeleting || isFetching}
                    onClick={() => setMessageToDelete(message)}
                >
                    Delete
                </Button>
            </td>
        );
    };

    return (
        <section className="bg-body-tertiary p-3">
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-body d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="h6 mb-0">Messages</h3>
                        <span className="small text-body-secondary">
                            Conversation #
                            {conversation.id}
                        </span>
                    </div>
                    {isError && <span className="badge text-bg-danger">Unable to load messages</span>}
                </div>
                <div className="card-body">
                    <OpenContentVisualizerContext.Provider value={openContentVisualizer}>
                        <AppGrid
                            scrollable="scrollable"
                            showLoader={isFetching}
                            dataItemKey="id"
                            data={data?.data ?? []}
                            sortable={true}
                            filterable={false}
                            resizable={true}
                            initialState={dataState}
                            onPageChange={(event: GridPageChangeEvent) => setDataState(previous => ({ ...previous, skip: event.page.skip, take: event.page.take }))}
                            onDataStateChange={(event: GridDataStateChangeEvent) => setDataState({ ...event.dataState, skip: event.dataState.skip, take: event.dataState.take })}
                            sort={dataState.sort}
                            total={data?.total ?? 0}
                            skip={dataState.skip}
                            take={dataState.take}
                            defaultSort={messageInitialSort}
                            onSortChange={(event: GridSortChangeEvent) => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }))}
                            pageable={{ buttonCount: 3, pageSizes: [10, 25, 50] }}
                        >
                            <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load messages. Please try again." : "No messages found for this conversation."}</span>}</GridNoRecords>
                            <GridColumn field="sequence" title="Sequence" width="120px" />
                            <GridColumn field="type" title="Type" cells={{ data: ChatMessageTypeCell }} width="140px" />
                            <GridColumn field="action" title="Action" width="180px" cells={{ data: p => <td className="k-table-td">{(p.dataItem as AdminChatConversationMessageDto).action || "-"}</td> }} />
                            <GridColumn title="Text" cells={{ data: TextCell }} width="280px" minWidth={110} />
                            <GridColumn title="Json" cells={{ data: JsonCell }} width="280px" minWidth={110} />
                            <GridColumn field="View Route" cells={{ data: RouteCell }} width="280px" minWidth={110} />
                            <GridColumn field="View Context" cells={{ data: ViewContextJsonCell }} width="280px" minWidth={110} />
                            <GridColumn field="createdAt" title="Created" width="220px" cells={{ data: DateCell<AdminChatConversationMessageDto>("created_at") }} />
                            <GridColumn title="Actions" width="130px" sortable={false} cells={{ data: DeleteMessageCell }} />
                        </AppGrid>
                    </OpenContentVisualizerContext.Provider>
                    <ContentVisualizer {...contentVisualizerProps} />
                    {messageToDelete !== null && (
                        <Dialog title="Permanently delete message?" width={520} onClose={() => !isDeleting && setMessageToDelete(null)}>
                            <p>
                                {`Delete message #${messageToDelete.id} (sequence ${messageToDelete.sequence}, ${getChatMessageTypeLabel(messageToDelete.type)}) from conversation #${conversation.id}?`}
                            </p>
                            {(messageToDelete.action || messageToDelete.text) && (
                                <p className="small text-body-secondary text-break">
                                    {messageToDelete.action ? `Action: ${messageToDelete.action}` : `Text: ${messageToDelete.text?.slice(0, 160)}`}
                                </p>
                            )}
                            <p className="mb-0 text-danger fw-semibold">This deletion is permanent and cannot be undone.</p>
                            <DialogActionsBar>
                                <Button type="button" disabled={isDeleting} onClick={() => setMessageToDelete(null)}>Cancel</Button>
                                <Button type="button" themeColor="error" svgIcon={trashIcon} disabled={isDeleting} onClick={() => void handleDeleteAsync()}>
                                    {isDeleting ? "Deleting..." : "Delete permanently"}
                                </Button>
                            </DialogActionsBar>
                        </Dialog>
                    )}
                </div>
            </div>
        </section>
    );
};

const ChatConversationManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [conversationToClone, setConversationToClone] = useState<AdminChatConversationDto | null>(null);
    const [detailExpand, setDetailExpand] = useState<Record<string, boolean>>({});
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });
    const request = { ...dataState, filter: buildFilter(conversationId, selectedCustomer, selectedUser, dataState.filter) };
    const { data, isFetching, isError } = useListAdminChatConversationsQuery(request);
    const [cloneConversation, { isLoading: isCloning }] = useCloneChatConversationMutation();
    const { data: customerData, isFetching: isCustomersFetching, isError: isCustomersError } = useListCustomersQuery(selectorRequest);
    const { data: userData, isFetching: isUsersFetching, isError: isUsersError } = useListUsersQuery(userSelectorRequest(selectedCustomer));
    const resetPaging = (): void => setDataState(previous => ({ ...previous, skip: 0 }));

    const handleConversationIdChange = (event: NumericTextBoxChangeEvent): void => {
        setConversationId(event.value);
        resetPaging();
    };

    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;
        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setSelectedUser(null);
        resetPaging();
    };

    const handleUserChange = (event: DropDownListChangeEvent): void => {
        const user = event.value as UserDto | null;
        setSelectedUser(user?.id == null ? null : user);
        resetPaging();
    };

    const handleCloneAsync = async (): Promise<void> => {
        if (conversationToClone == null || isCloning) {
            return;
        }

        const sourceConversationId = conversationToClone.id;
        try {
            const response = await cloneConversation({ chat_conversation_id: sourceConversationId }).unwrap();
            setConversationToClone(null);
            dispatch(notify(`Conversation #${sourceConversationId} was cloned as conversation #${response.chat_conversation_id}.`, "success"));
        } catch (error) {
            dispatch(notify(getMutationErrorMessage(error, "clone"), "error"));
        }
    };

    const CloneConversationCell = (cellProps: GridCellProps): React.ReactElement => {
        const conversation = cellProps.dataItem as AdminChatConversationDto;

        return (
            <td className="k-table-td text-center align-middle">
                <Button
                    type="button"
                    themeColor="primary"
                    fillMode="outline"
                    size="small"
                    svgIcon={copyIcon}
                    disabled={isCloning || isFetching}
                    onClick={() => setConversationToClone(conversation)}
                >
                    Clone
                </Button>
            </td>
        );
    };

    return (
        <div className="d-flex flex-column gap-3 p-3 h-100 min-h-0 overflow-hidden">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-chat-conversation-id">Conversation ID</label>
                    <NumericTextBox id="admin-chat-conversation-id" value={conversationId} min={1} format="n0" onChange={handleConversationIdChange} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-chat-customer">Customer</label>
                    <DropDownList id="admin-chat-customer" data={customerData?.data ?? []} textField="name" dataItemKey="id" value={selectedCustomer} onChange={handleCustomerChange} disabled={isCustomersFetching} defaultItem={{ id: 0, name: isCustomersFetching ? "Loading customers..." : "All customers" }} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-chat-user">User</label>
                    <DropDownList id="admin-chat-user" data={userData?.data ?? []} textField="email" dataItemKey="id" value={selectedUser} onChange={handleUserChange} disabled={selectedCustomer == null || isUsersFetching} defaultItem={{ id: "", email: isUsersFetching ? "Loading users..." : selectedCustomer == null ? "Select customer first" : "All users" }} />
                </div>
            </div>
            {(isError || isCustomersError || isUsersError) && <div className="alert alert-danger mb-0" role="alert">Unable to load conversations, customers, or users. Please try again.</div>}
            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    scrollable="scrollable"
                    showLoader={isFetching || isCustomersFetching || isUsersFetching}
                    dataItemKey="id"
                    data={data?.data ?? []}
                    sortable={true}
                    filterable={false}
                    resizable={true}
                    initialState={dataState}
                    onPageChange={(event: GridPageChangeEvent) => setDataState(previous => ({ ...previous, skip: event.page.skip, take: event.page.take }))}
                    onDataStateChange={(event: GridDataStateChangeEvent) => setDataState({ ...event.dataState, skip: event.dataState.skip, take: event.dataState.take })}
                    detail={ConversationMessages}
                    detailExpand={detailExpand}
                    onDetailExpandChange={(event: GridDetailExpandChangeEvent) => setDetailExpand(event.detailExpand)}
                    sort={dataState.sort}
                    total={data?.total ?? 0}
                    skip={dataState.skip}
                    take={dataState.take}
                    defaultSort={initialSort}
                    onSortChange={(event: GridSortChangeEvent) => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }))}
                    pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                    style={{ height: "100%" }}
                >
                    <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load conversations. Please try again." : "No conversations found for the selected filters."}</span>}</GridNoRecords>
                    <GridColumn field="id" title="ID" width="100px" />
                    <GridColumn field="customerName" title="Customer" cells={{ data: p => <td className="k-table-td">{(p.dataItem as AdminChatConversationDto).customer_name}</td> }} />
                    <GridColumn field="userEmail" title="User" cells={{ data: p => <td className="k-table-td">{(p.dataItem as AdminChatConversationDto).user_email}</td> }} />
                    <GridColumn field="messageCount" title="Messages" width="120px" cells={{ data: p => <td className="k-table-td text-end">{(p.dataItem as AdminChatConversationDto).message_count}</td> }} />
                    <GridColumn field="createdAt" title="Created" width="220px" cells={{ data: DateCell<AdminChatConversationDto>("created_at") }} />
                    <GridColumn field="lastMessageAt" title="Last Message" width="220px" cells={{ data: DateCell<AdminChatConversationDto>("last_message_at") }} />
                    <GridColumn title="Actions" width="130px" sortable={false} cells={{ data: CloneConversationCell }} />
                </AppGrid>
            </div>
            {conversationToClone !== null && (
                <Dialog title="Clone conversation?" width={560} onClose={() => !isCloning && setConversationToClone(null)}>
                    <p>
                        {`Clone conversation #${conversationToClone.id} for ${conversationToClone.user_email}?`}
                    </p>
                    <p className="mb-0">
                        The complete persisted conversation tree and all related records will be copied to a new conversation. The original conversation will not be changed.
                    </p>
                    <DialogActionsBar>
                        <Button type="button" disabled={isCloning} onClick={() => setConversationToClone(null)}>Cancel</Button>
                        <Button type="button" themeColor="primary" svgIcon={copyIcon} disabled={isCloning} onClick={() => void handleCloneAsync()}>
                            {isCloning ? "Cloning..." : "Clone conversation"}
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default ChatConversationManagement;
